/**
 * RecentlyAddedPage
 *
 * Journal articles added within the last 7 days. The "created after" cutoff is
 * applied by the shared context's fetchPage based on the "recently-added"
 * sidebar option; the page itself is a standard table view.
 */

import { useMemo } from "react"
import JournalArticlesShell from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "Recently Added"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles that have been added to our database in the past week"

/**
 * The Recently Added page.
 *
 * @returns The rendered page element.
 */
export default function RecentlyAddedPage(): React.JSX.Element {
  const page = useJournalArticlesPage({ sidebarOption: "recently-added" })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <JournalArticlesShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <JournalArticlesTable page={page} filterOptions={filterOptions} visibleColumns={visibleColumns} />
    </JournalArticlesShell>
  )
}
